import type { CsatTriggerPayload } from "@ts/interfaces/terms/api/termsApiInterface";

// Window.Usersnap is declared (with full type) in src/ts/types/usersnap.ts.
// window.__csatPendingPayload is declared in src/vite-env.d.ts.

/**
 * Open a Usersnap widget programmatically by ID.
 *
 * Pre-populates the euka_id and email custom fields before opening so
 * submissions are always identifiable. Extracted from HelpButton so
 * other surfaces (e.g. the Read Aloud notification panel) can open
 * feedback forms without coupling to the HelpButton component.
 *
 * @param widgetId     Usersnap widget UUID (from env.config).
 * @param eukaId       Student LMS username — used to pre-fill euka_id field.
 * @param parentEmail  Parent email — used to pre-fill email field.
 */
export const openUsersnapWidget = (
  widgetId: string,
  eukaId = "",
  parentEmail = ""
): void => {
  setUsersnapField({ euka_id: eukaId, email: parentEmail });
  const usersnap = window.Usersnap;
  if (usersnap && typeof usersnap.show === "function") {
    usersnap
      .show(widgetId)
      .then(widgetApi => {
        widgetApi.open();
        widgetApi.on("close", () => {
          usersnap.hide(widgetId);
        });
      })
      .catch(error => {
        console.error("Failed to open Usersnap widget:", error);
      });
  } else {
    console.error("Usersnap is not loaded or not properly initialized");
  }
};

/**
 * Open the Read Aloud "User Suggested Feedback" Usersnap form.
 *
 * Pre-populates the Usersnap built-in labels field and the custom fields
 * mandated by BLD2-461 spec (`labels: ["Lesson: Read aloud"]`,
 * `lms_platform: "OEX"`) and opens the widget by its dedicated project API key.
 *
 * Failure contract: SDK unavailable or show() rejection both fail silently —
 * no visible error is shown to the student. This is intentional per the spec.
 *
 * Does NOT dismiss the notification panel — that is X-button only.
 *
 * @param widgetId  Usersnap widget UUID from env.config
 *                  (USERSNAP_READ_ALOUD_FEEDBACK_WIDGET_ID).
 */
export const openReadAloudFeedback = (widgetId: string): void => {
  setUsersnapLabels(["Lesson: Read aloud"]);
  setUsersnapField({ lms_platform: "OEX" });
  const usersnap = window.Usersnap;
  if (usersnap && typeof usersnap.show === "function") {
    usersnap
      .show(widgetId)
      .then(widgetApi => {
        widgetApi.open();
        widgetApi.on("close", () => {
          usersnap.hide(widgetId);
        });
      })
      .catch(() => {
        // SDK failure fails silently — no visible error to student (BLD2-461 spec)
      });
  }
  // No else branch — silent failure when SDK is unavailable (BLD2-461 spec)
};

/**
 * Pre-populate a Usersnap form's custom fields on widget open.
 * Called just before programmatically opening a help/feedback widget.
 */
export const setUsersnapField = (fields: Record<string, string>) => {
  const { Usersnap } = window;
  if (Usersnap && typeof Usersnap.on === "function") {
    Usersnap.on("open", event => {
      event.api.setValue("custom", fields);
    });
  }
};

/**
 * Pre-populate Usersnap's built-in labels field on widget open.
 * Labels appear as tags on the submission in the Usersnap dashboard and
 * support filtering — distinct from the freeform `custom` data object.
 * Called just before programmatically opening a feedback widget.
 */
export const setUsersnapLabels = (labels: string[]) => {
  const { Usersnap } = window;
  if (Usersnap && typeof Usersnap.on === "function") {
    Usersnap.on("open", event => {
      event.api.setValue("labels", labels);
    });
  }
};

/**
 * Holds the most recent CSAT payload so the registered on("open") handler
 * always submits up-to-date data regardless of how many times
 * fireUsersnapCsatEvent is called per session.
 */
let csatPayloadRef: CsatTriggerPayload | null = null;

/**
 * Whether we've already registered the single on("open") handler for CSAT
 * custom data. Prevents listener accumulation across multiple calls to
 * fireUsersnapCsatEvent within the same session.
 */
let csatOpenHandlerRegistered = false;

/**
 * Resets CSAT module-level state. Available in test environments only —
 * resolves to undefined in production builds so it tree-shakes cleanly.
 * @internal
 */
export const _resetCsatState =
  import.meta.env.MODE === "test"
    ? () => {
        csatPayloadRef = null;
        csatOpenHandlerRegistered = false;
        window.__csatPendingPayload = undefined;
      }
    : undefined;

/**
 * Fires the Usersnap CSAT survey trigger event.
 *
 * If the SDK is fully initialised (window.Usersnap is set and logEvent is
 * available), fires immediately. Otherwise parks the payload in
 * window.__csatPendingPayload — usersnap-init.js drains this once api.init()
 * resolves and window.Usersnap is ready.
 *
 * Registers a one-time on("open") handler (if not already registered) that
 * attaches the CSAT payload as custom data to the survey submission.
 *
 * @returns true if logEvent was called immediately, false if queued for drain.
 */
export const fireUsersnapCsatEvent = (payload: CsatTriggerPayload): boolean => {
  const { Usersnap } = window;

  // Always update the refs so the open-handler and the drain both send
  // the current payload regardless of which path fires logEvent.
  csatPayloadRef = payload;

  if (!Usersnap || typeof Usersnap.logEvent !== "function") {
    // SDK not ready yet — park the payload for usersnap-init.js to drain
    // once api.init() resolves. The on("open") handler registration also
    // happens at drain time via the same api reference.
    window.__csatPendingPayload = payload;
    return false;
  }

  // Register the on("open") handler exactly once per session
  if (!csatOpenHandlerRegistered && typeof Usersnap.on === "function") {
    csatOpenHandlerRegistered = true;
    Usersnap.on("open", event => {
      if (csatPayloadRef) {
        // Spread all payload fields except should_fire (internal flag, not
        // useful as survey custom data). New fields on CsatTriggerPayload
        // will be included automatically without needing to update this site.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { should_fire: _shouldFire, ...customData } = csatPayloadRef;
        event.api.setValue("custom", customData);
      }
    });
  }

  Usersnap.logEvent("student_lesson_csat_trigger");
  return true;
};
